/**
 * The archived entries of the app's Status, Priority, Category and Type lists. Those lists hold only live entries,
 * because every picker built from them must offer only those - but a ticket can still hold an archived one, and the
 * lists, boards, My Work and phone cards name a ticket's value by looking it up there, so it showed blank. Each live
 * list is paired here with its archived entries, so the name lookups (getStatus, getCategory, showParentChild ...)
 * fall back to them while the pickers are untouched.
 */

interface LookupRecord {
  Id: number;
  IsArchived?: boolean;
}

const archivedFor = new WeakMap<object, LookupRecord[]>();

/**
 * Splits one load of a list (everything not deleted) into the live entries, returned for app state, and the archived
 * ones, kept here against that array.
 */
export const liveWithArchived = <T extends LookupRecord>(records: T[] | null | undefined): T[] => {
  if (!Array.isArray(records)) return records;
  const live = records.filter(record => !record.IsArchived);
  archivedFor.set(live, records.filter(record => record.IsArchived));
  return live;
};

/** A live list loaded on its own, paired with its archived entries loaded separately (loadFilters, #28462). */
export const pairArchived = <T extends LookupRecord>(live: T[] | null | undefined, archived: T[] | null | undefined): T[] => {
  if (!Array.isArray(live)) return live;
  archivedFor.set(live, Array.isArray(archived) ? archived : []);
  return live;
};

/** The archived entries kept for this live list, if any. */
export const archivedOf = <T>(live: T[] | null | undefined): T[] =>
  (live && (archivedFor.get(live) as T[] | undefined)) ?? [];

/** An entry by id: the live list first, then its archived entries. */
export const findWithArchived = <T extends { Id?: number }>(live: T[] | null | undefined, id: unknown): T | undefined =>
  !Array.isArray(live) ? undefined : live.find(record => record?.Id == id) ?? archivedOf(live).find(record => record?.Id == id);
